import React, { useState, useRef, useEffect } from 'react';
import { StyledSelect, StyledSelected, StyledOptions, StyledOption } from './dropDown.style';
import { LucideChevronDown, LucideLanguages } from 'lucide-react';

const DropDown = ({
  options = [],
  selectedOption = () => {},
  label = 'Select',
  width = '150px',
}) => {
  const [selected, setSelected] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const optionsRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus management
  useEffect(() => {
    if (isOpen && optionsRef.current) {
      const optionsNode = optionsRef.current.querySelectorAll('[role="option"]');
      if (focusedIndex >= 0 && optionsNode[focusedIndex]) {
        optionsNode[focusedIndex].focus();
      }
    }
  }, [isOpen, focusedIndex]);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setFocusedIndex(0);
        } else if (focusedIndex >= 0) {
          handleSelect(options[focusedIndex]);
        }
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setFocusedIndex(0);
        } else {
          setFocusedIndex((prev) => (prev + 1) % options.length);
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setFocusedIndex(options.length - 1);
        } else {
          setFocusedIndex((prev) => (prev - 1 + options.length) % options.length);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setFocusedIndex(-1);
        if (dropdownRef.current) {
          dropdownRef.current.focus();
        }
        break;
      case 'Tab':
        setIsOpen(false);
        setFocusedIndex(-1);
        break;
    }
  };

  const handleSelect = (option) => {
    setSelected(option.label);
    selectedOption(option);
    setIsOpen(false);
    setFocusedIndex(-1);
    if (dropdownRef.current) {
      dropdownRef.current.focus();
    }
  };

  return (
    <StyledSelect
      ref={dropdownRef}
      style={{ width }}
      tabIndex={0}
      role="combobox"
      aria-expanded={isOpen}
      aria-haspopup="listbox"
      aria-controls="dropdown-options"
      aria-label={label}
      onKeyDown={handleKeyDown}
      className="language_dropdown"
    >
      <StyledSelected
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label={`Selected: ${selected || label}`}
      >
        <LucideLanguages style={{ width: '18px', height: '18px' }} aria-hidden="true" />
        {selected || label}
        <LucideChevronDown style={{ width: '18px', height: '18px' }} aria-hidden="true" />
      </StyledSelected>

      {isOpen && (
        <StyledOptions
          ref={optionsRef}
          role="listbox"
          id="dropdown-options"
          aria-label={`${label} options`}
          className="hide-scroll-bar"
        >
          {options.map((option, index) => (
            <StyledOption
              key={option.value}
              role="option"
              tabIndex={0}
              aria-selected={selected === option.label}
              onClick={() => handleSelect(option)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSelect(option);
                }
              }}
              className={focusedIndex === index ? 'focused' : ''}
            >
              {option.icon && (
                <span style={{ width: '20px', height: '20px' }} aria-hidden="true">
                  {option.icon}
                </span>
              )}

              {option.label}
            </StyledOption>
          ))}
        </StyledOptions>
      )}
    </StyledSelect>
  );
};

export default DropDown;
